import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useWarehouseId } from '@/contexts/WarehouseContext';
import { useSessionState } from '@/hooks/useSessionState';
import type { ProductConfigPayload } from '@/modules/supply/components/ProductConfigStrip';
import type { LoadEstimateSupplierResult } from '@/modules/supply/utils/loadEstimate';
import {
  LOAD_ESTIMATE_KEY as T, containerCodeOf, draftPayload, errorMessage, findProductConflicts, friendlyReason, num, totalsOf,
  type LoadEstimateRow,
} from '@/modules/supply/utils/loadEstimateDraft';
import { snapshotOf } from '@/modules/supply/utils/loadEstimateOccupancy';
import { loadEstimateService, type LoadEstimateDraft } from '@/services/loadEstimateService';
import { supplyAgentService, type SupplyAgentLogisticsCalculateResponse } from '@/services/supplyAgentService';

/** Proveedores que se estiman a la vez al entrar. */
const ESTIMATE_BATCH_SIZE = 4;

/**
 * Estado del módulo de estimación de carga de un almacén: pedidos validados, estimación de cada proveedor, proveedores
 * marcados y borrador guardado. Lo derivado del camión (consolidación, restricción logística) está en useLoadEstimateTruck.
 */
export function useLoadEstimate() {
  const { t, i18n } = useTranslation();
  const warehouseId = useWarehouseId();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  // Proveedores que el comprador estaba viendo en la bandeja al pulsar «Estimar carga».
  const requestedSupplierIds = useMemo(
    () => (searchParams.get('suppliers') ?? '').split(',').map(Number).filter((id) => Number.isInteger(id) && id > 0),
    [searchParams],
  );
  // Misma preferencia de MP/MOQ que la bandeja: define la cantidad que acaba en la orden.
  const [applyMoqMp] = useSessionState<boolean>('supply_applyMoqMp', true);
  // Proveedores marcados: los que entran en la orden. El resto de la tabla solo se estima.
  const [marked, setMarked] = useState<number[]>([]);
  // Proveedores sin pedido validado que se añadieron con el buscador (se estima su pedido sugerido).
  const [extraIds, setExtraIds] = useState<number[]>([]);
  const [sources, setSources] = useState<LoadEstimateSupplierResult[]>([]);
  const [activeSupplierId, setActiveSupplierId] = useState<number | null>(null);
  const [calculating, setCalculating] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);
  const [restoreNotice, setRestoreNotice] = useState<string | null>(null);
  const [heldDraft, setHeldDraft] = useState<LoadEstimateDraft | null>(null);
  const [bootstrapped, setBootstrapped] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const draftRef = useRef<LoadEstimateDraft | null>(null);
  const lastSaved = useRef<string | null>(null);
  const saveTimer = useRef<number | undefined>(undefined);
  const saveChain = useRef<Promise<unknown>>(Promise.resolve());
  const restoredFor = useRef<number | null>(null);
  const inFlight = useRef(new Set<number>());
  // Cantidades y contenedores de un borrador retomado: se aplican cuando se estima cada proveedor.
  const initialRef = useRef<{ overrides: Record<number, Record<string, number>>; containers: Record<number, number> }>({ overrides: {}, containers: {} });

  const enabled = typeof warehouseId === 'number';
  const pendingQuery = useQuery({
    queryKey: ['loadEstimate', 'pendingSuppliers', warehouseId, applyMoqMp],
    queryFn: () => loadEstimateService.pendingSuppliers(warehouseId as number, applyMoqMp),
    enabled,
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const abcQuery = useQuery({
    queryKey: ['loadEstimate', 'productAbc', warehouseId],
    queryFn: () => loadEstimateService.productAbc(warehouseId as number),
    enabled,
  });
  const draftsQuery = useQuery({
    queryKey: ['loadEstimate', 'drafts', warehouseId],
    queryFn: () => loadEstimateService.listDrafts(warehouseId as number),
    enabled,
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const awaitingQuery = useQuery({
    queryKey: ['loadEstimate', 'awaitingValidation', warehouseId, applyMoqMp],
    queryFn: () => loadEstimateService.awaitingValidation(warehouseId as number, applyMoqMp),
    enabled,
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const templatesQuery = useQuery({
    queryKey: ['loadEstimate', 'containerTemplates'],
    queryFn: () => supplyAgentService.logisticsContainerTemplates(),
    enabled,
  });

  const pending = useMemo(() => pendingQuery.data ?? [], [pendingQuery.data]);
  const abcByProduct = useMemo(() => abcQuery.data ?? {}, [abcQuery.data]);
  const awaiting = useMemo(() => awaitingQuery.data ?? [], [awaitingQuery.data]);
  // Se estima el pedido validado de un proveedor y, si no tiene, su pedido sugerido.
  const candidates = useMemo(() => [...pending, ...awaiting], [pending, awaiting]);
  const templates = useMemo(() => templatesQuery.data?.results ?? [], [templatesQuery.data]);

  // Filas de la tabla: los proveedores con pedido validado y los que se añadieron con el buscador.
  const rowIds = useMemo(() => {
    const validated = pending.map((row) => row.supplier_id);
    const extras = extraIds.filter((id) => !validated.includes(id) && awaiting.some((row) => row.supplier_id === id));
    return [...validated, ...extras];
  }, [pending, extraIds, awaiting]);

  // Productos del pedido validado de un proveedor; si se retoma un borrador o se cambia el contenedor, con las cantidades vigentes.
  const expectedFor = useCallback((supplierId: number, saved?: Record<string, number>) => {
    const lines = candidates.find((row) => row.supplier_id === supplierId)?.lines ?? [];
    return saved
      ? Object.entries(saved).map(([productId, quantity]) => {
          const known = lines.find((line) => line.product_id === Number(productId));
          return { product_id: Number(productId), sku: known?.sku ?? '', product_name: known?.product_name ?? `#${productId}`, quantity };
        })
      : lines;
  }, [candidates]);

  // Cada proveedor se calcula con su propio contenedor y por separado: si uno falla, los demás siguen y sus productos no se pierden.
  const calculateSuppliers = useCallback(async (
    supplierIds: number[],
    overrides?: Record<number, Record<string, number>>,
    containers?: Record<number, number>,
  ): Promise<LoadEstimateSupplierResult[]> => {
    const settled = await Promise.allSettled(supplierIds.map((supplierId) => supplyAgentService.logisticsCalculate({
      mode: 'SIMULATE',
      supplier_id: supplierId,
      warehouse_id: warehouseId as number,
      load_suggested_order: true,
      locale: i18n.language,
      only_product_ids: expectedFor(supplierId, overrides?.[supplierId]).map((line) => line.product_id),
      // Siempre con las cantidades de la orden (con MP/MOQ): el motor usaría su propia sugerencia sin ellas.
      quantity_overrides: overrides?.[supplierId]
        ?? Object.fromEntries(expectedFor(supplierId).map((line) => [String(line.product_id), line.quantity])),
      ...(containers?.[supplierId] ? { container_template_id: containers[supplierId] } : {}),
    })));
    const names = new Map(candidates.map((row) => [row.supplier_id, row.supplier_name]));
    return supplierIds.map((supplierId, index) => {
      const supplierName = names.get(supplierId) || String(supplierId);
      const outcome = settled[index];
      const calculated = outcome.status === 'fulfilled' && outcome.value.container ? outcome.value : null;
      const unavailableReason = calculated
        ? undefined
        : outcome.status === 'rejected'
          ? friendlyReason(errorMessage(outcome.reason, t('supply.supplyAgent.logisticsSimulateError')), t)
          : t('supply.supplyAgent.loadEstimateSupplierUnavailable', { supplier: supplierName });
      // Los productos validados sin datos logísticos no entran al cálculo, pero siguen en el pedido: se
      // añaden como líneas incompletas para verlos y para que la orden no los pierda.
      const eligible = calculated?.before?.lines_detail ?? [];
      const done = new Set(eligible.map((line) => num(line.product_id)));
      const missing = expectedFor(supplierId, overrides?.[supplierId])
        .filter((line) => !done.has(line.product_id))
        .map((line) => ({
          product_id: line.product_id,
          product_name: line.product_name,
          sku: line.sku,
          supplier_id: supplierId,
          supplier_name: supplierName,
          suggested_units: line.quantity,
          suggested_quantity: line.quantity,
        }));
      const lines = [...eligible, ...missing];
      const result = calculated
        ? (missing.length > 0 ? { ...calculated, before: { ...calculated.before, lines_detail: lines } } : calculated)
        : ({ mode: 'SIMULATE', status: 'SUCCESS_WITH_WARNINGS', before: snapshotOf(lines), after: snapshotOf(lines), lines_detail: lines } as unknown as SupplyAgentLogisticsCalculateResponse);
      return { supplierId, supplierName, result, unavailableReason };
    });
  }, [warehouseId, i18n.language, candidates, expectedFor, t]);

  // Productos del pedido (sugerido o validado) de más de un proveedor marcado: el borrador solo admite una línea por
  // producto, así que no se puede guardar hasta elegir un único proveedor para cada uno.
  const productConflicts = useMemo(() => findProductConflicts(sources, marked), [sources, marked]);

  // --- Guardado del borrador: solo con los proveedores marcados, en orden y sin duplicar el borrador ---
  // Devuelve si el borrador quedó guardado (false si falló: el error ya se muestra).
  const persist = useCallback((nextSources: LoadEstimateSupplierResult[], nextMarked: number[]) => {
    const run = async (): Promise<boolean> => {
      const conflicts = findProductConflicts(nextSources, nextMarked);
      if (conflicts.length > 0) {
        setSaveStatus('idle');
        setError(t(`${T}.productConflictError`, { product: conflicts[0].productName, count: conflicts.length }));
        return false;
      }
      // El conflicto (u otro error de guardado anterior) ya no aplica: se limpia antes de reintentar.
      setError((current) => (current ? null : current));
      const draft = draftRef.current;
      const payload = draftPayload(nextSources.filter((source) => nextMarked.includes(source.supplierId)), draft, applyMoqMp);
      if (!draft && payload.lines.length === 0) return true;
      const key = JSON.stringify(payload);
      if (draft && key === lastSaved.current) return true;
      setSaveStatus('saving');
      try {
        draftRef.current = draft
          ? await loadEstimateService.update(draft.id, payload)
          : await loadEstimateService.create({ warehouse_id: warehouseId as number, ...payload });
        lastSaved.current = key;
        setSaveStatus('saved');
        return true;
      } catch (e) {
        setSaveStatus('idle');
        setError(errorMessage(e, t(`${T}.saveError`)));
        return false;
      }
    };
    const result = saveChain.current.then(run, run);
    saveChain.current = result;
    return result;
  }, [warehouseId, applyMoqMp, t]);
  const persistRef = useRef(persist);
  persistRef.current = persist;
  const latest = useRef({ sources, marked });
  latest.current = { sources, marked };
  const dirty = useRef(false);

  // Cada cambio de lo marcado o de las cantidades se guarda en segundo plano, cuando todos los marcados ya están estimados.
  useEffect(() => {
    if (!bootstrapped) return undefined;
    if (marked.some((id) => !sources.some((source) => source.supplierId === id))) return undefined;
    dirty.current = true;
    window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      dirty.current = false;
      void persist(sources, marked);
    }, 600);
    return () => window.clearTimeout(saveTimer.current);
  }, [bootstrapped, marked, sources, persist]);
  // Al salir del módulo no se pierde el último ajuste.
  useEffect(() => () => {
    if (dirty.current) void persistRef.current(latest.current.sources, latest.current.marked);
  }, []);

  // Estima los proveedores de la tabla que aún no tienen estimación.
  useEffect(() => {
    if (!bootstrapped || !enabled) return;
    const missing = rowIds.filter((id) => !sources.some((source) => source.supplierId === id) && !inFlight.current.has(id));
    if (missing.length === 0) return;
    missing.forEach((id) => inFlight.current.add(id));
    setCalculating(true);
    const { overrides, containers } = initialRef.current;
    // Por tandas: no se lanzan decenas de cálculos a la vez y cada proveedor aparece en cuanto su tanda termina.
    void (async () => {
      for (let start = 0; start < missing.length; start += ESTIMATE_BATCH_SIZE) {
        const next = await calculateSuppliers(missing.slice(start, start + ESTIMATE_BATCH_SIZE), overrides, containers);
        setSources((current) => [...current.filter((source) => !next.some((item) => item.supplierId === source.supplierId)), ...next]);
        setActiveSupplierId((current) => current ?? next.find((source) => !source.unavailableReason)?.supplierId ?? null);
      }
    })().catch((e) => {
      setError(errorMessage(e, t('supply.supplyAgent.logisticsSimulateError')));
    }).finally(() => {
      missing.forEach((id) => inFlight.current.delete(id));
      if (inFlight.current.size === 0) setCalculating(false);
    });
  }, [bootstrapped, enabled, rowIds, sources, calculateSuppliers, t]);

  // Lo que ya no está en la tabla (p. ej. un proveedor cuya orden se creó) deja de estimarse y de marcarse.
  useEffect(() => {
    if (!bootstrapped || pendingQuery.isFetching || awaitingQuery.isFetching) return;
    setSources((current) => (current.some((source) => !rowIds.includes(source.supplierId))
      ? current.filter((source) => rowIds.includes(source.supplierId))
      : current));
    setMarked((current) => (current.some((id) => !rowIds.includes(id)) ? current.filter((id) => rowIds.includes(id)) : current));
  }, [bootstrapped, rowIds, pendingQuery.isFetching, awaitingQuery.isFetching]);

  // Retoma un borrador guardado: lo compara con el pedido actual y marca sus proveedores con sus cantidades y contenedores.
  const restoreDraft = useCallback((draft: LoadEstimateDraft) => {
    draftRef.current = draft;
    // El pedido validado pudo cambiar desde la última vez: se compara con el borrador para no crear una orden con
    // productos que ya no están validados ni perder los nuevos.
    const current = new Map(candidates.map((row) => [row.supplier_id, new Map(row.lines.map((line) => [line.product_id, line.quantity]))]));
    const overrides: Record<number, Record<string, number>> = {};
    const baseline = new Map<number, number>();
    let removed = 0;
    let changed = 0;
    let added = 0;
    draft.lines.forEach((line) => {
      const now = current.get(line.supplier_id)?.get(line.product_id);
      if (now == null) { removed += 1; return; }
      const wasSuggested = num(line.suggested_quantity);
      const quantity = Math.abs(now - wasSuggested) > 1e-9 ? now : num(line.quantity);
      if (quantity === now && now !== num(line.quantity)) changed += 1;
      if (Math.abs(now - wasSuggested) > 1e-9) baseline.set(line.product_id, now);
      overrides[line.supplier_id] = { ...overrides[line.supplier_id], [String(line.product_id)]: quantity };
    });
    new Set(draft.lines.map((line) => line.supplier_id)).forEach((supplierId) => {
      current.get(supplierId)?.forEach((quantity, productId) => {
        if (overrides[supplierId]?.[String(productId)] != null) return;
        overrides[supplierId] = { ...overrides[supplierId], [String(productId)]: quantity };
        added += 1;
      });
    });
    const supplierIds = Object.keys(overrides).map(Number);
    if (supplierIds.length === 0) {
      // Nada de lo estimado sigue validado: el borrador no sirve.
      draftRef.current = null;
      lastSaved.current = null;
      void loadEstimateService.discard(draft.id);
      setRestoreNotice(t(`${T}.restoreEmpty`));
      return;
    }
    if (removed + changed + added > 0) setRestoreNotice(t(`${T}.restoreChanged`, { removed, changed, added }));
    // La cantidad validada de partida de los productos que cambiaron en la bandeja es la actual.
    draftRef.current = { ...draft, lines: draft.lines.map((line) => (baseline.has(line.product_id) ? { ...line, suggested_quantity: String(baseline.get(line.product_id)) } : line)) };
    lastSaved.current = null;

    const containers: Record<number, number> = {};
    supplierIds.forEach((supplierId) => {
      const template = templates.find((item) => item.code === draft.container_by_supplier?.[String(supplierId)]);
      if (template) containers[supplierId] = template.id;
    });
    initialRef.current = { overrides, containers };
    setMarked(supplierIds);
    setExtraIds((currentIds) => [...new Set([...currentIds, ...supplierIds.filter((id) => !pending.some((row) => row.supplier_id === id))])]);
    // Se vuelven a estimar con las cantidades y el contenedor del borrador.
    setSources((currentSources) => currentSources.filter((source) => !supplierIds.includes(source.supplierId)));
  }, [templates, candidates, pending, t]);

  // Al entrar (o recargar) se retoma el borrador guardado y se marcan los proveedores elegidos en la bandeja.
  // Si se llega desde la bandeja con otros proveedores, manda lo elegido: el borrador se ofrece retomar.
  useEffect(() => {
    if (!enabled || restoredFor.current === warehouseId) return;
    if (!draftsQuery.data || draftsQuery.isFetching || pendingQuery.isFetching || awaitingQuery.isFetching || templatesQuery.isLoading) return;
    restoredFor.current = warehouseId as number;
    const draft = draftsQuery.data[0];
    draftRef.current = draft ?? null;
    const requested = requestedSupplierIds.filter((id) => pending.some((row) => row.supplier_id === id));
    let restored = false;
    if (draft && draft.lines.length > 0) {
      const saved = new Set(draft.lines.map((line) => line.supplier_id));
      const same = requested.length === 0 || (requested.length === saved.size && requested.every((id) => saved.has(id)));
      if (same) {
        restoreDraft(draft);
        restored = true;
      } else {
        setHeldDraft(draft);
      }
    }
    if (!restored && requested.length > 0) setMarked(requested);
    setBootstrapped(true);
  }, [enabled, warehouseId, draftsQuery.data, draftsQuery.isFetching, pendingQuery.isFetching, awaitingQuery.isFetching, templatesQuery.isLoading, requestedSupplierIds, pending, restoreDraft]);

  // Vuelve a calcular la carga de un proveedor con sus cantidades vigentes, con su contenedor o con otro elegido.
  // Lanza el error del cálculo para que quien llama decida cómo mostrarlo.
  const recalculateSupplier = async (supplierId: number, templateId?: number) => {
    const source = sources.find((item) => item.supplierId === supplierId);
    if (!source) return;
    const quantities = Object.fromEntries((source.result.before?.lines_detail ?? []).map((line) => [
      String(line.product_id), num(line.suggested_units ?? line.suggested_quantity),
    ]));
    const template = templateId ?? templates.find((item) => item.code === containerCodeOf(source))?.id;
    const [updated] = await calculateSuppliers([supplierId], { [supplierId]: quantities }, template ? { [supplierId]: template } : undefined);
    if (updated.unavailableReason) throw new Error(`${updated.supplierName}: ${updated.unavailableReason}`);
    setSources((current) => current.map((item) => (item.supplierId === supplierId ? updated : item)));
  };

  // Cambiar el contenedor de un proveedor recalcula su carga con las cantidades vigentes.
  const changeContainer = async (supplierId: number, templateId: number) => {
    setCalculating(true);
    setError(null);
    try {
      await recalculateSupplier(supplierId, templateId);
    } catch (e) {
      setError(errorMessage(e, t('supply.supplyAgent.logisticsSimulateError')));
    } finally {
      setCalculating(false);
    }
  };

  // Corregir las medidas de un producto desde la estimación: se guardan en el maestro y se recalcula su proveedor.
  const saveProductConfig = async (payload: ProductConfigPayload) => {
    await supplyAgentService.logisticsPatchProductPackaging(payload);
    await recalculateSupplier(payload.supplier_id);
  };

  const toggleMarked = (supplierId: number) => setMarked((current) => (
    current.includes(supplierId) ? current.filter((id) => id !== supplierId) : [...current, supplierId]
  ));

  // Añade con el buscador un proveedor sin pedido validado: se estima su pedido sugerido y queda marcado.
  const addSupplier = (supplierId: number) => {
    setExtraIds((current) => (current.includes(supplierId) ? current : [...current, supplierId]));
    setMarked((current) => (current.includes(supplierId) ? current : [...current, supplierId]));
    setActiveSupplierId(supplierId);
  };
  const removeSupplier = (supplierId: number) => {
    setExtraIds((current) => current.filter((id) => id !== supplierId));
    setMarked((current) => current.filter((id) => id !== supplierId));
    setSources((current) => current.filter((source) => source.supplierId !== supplierId));
  };

  const createOrder = async () => {
    setCreating(true);
    setError(null);
    window.clearTimeout(saveTimer.current);
    try {
      // Si el guardado falla no se crea la orden: saldría con las líneas viejas del borrador.
      if (!(await persist(sources, marked))) return;
      const draft = draftRef.current;
      if (!draft) return;
      const response = await loadEstimateService.createOrders(draft.id);
      draftRef.current = null;
      lastSaved.current = null;
      dirty.current = false;
      setSaveStatus('idle');
      setMarked([]);
      setCreated(`${t(`${T}.created`, { created: response.total_created, updated: response.total_updated })}${response.skipped_products ? ` ${t(`${T}.createdSkipped`, { count: response.skipped_products })}` : ''}`);
      initialRef.current = { overrides: {}, containers: {} };
      await queryClient.invalidateQueries({ queryKey: ['loadEstimate'] });
      await queryClient.invalidateQueries({ queryKey: ['pendingOrders'] });
      // Los proveedores que se pidieron ya no están validados; el resto se vuelve a estimar con el pedido actual.
      setSources([]);
      setActiveSupplierId(null);
    } catch (e) {
      setError(errorMessage(e, t(`${T}.createError`)));
    } finally {
      setCreating(false);
    }
  };

  // Descartar la estimación guardada: se desmarca todo y las cantidades vuelven al pedido validado.
  const discardEstimate = async () => {
    window.clearTimeout(saveTimer.current);
    dirty.current = false;
    const draft = draftRef.current;
    draftRef.current = null;
    lastSaved.current = null;
    initialRef.current = { overrides: {}, containers: {} };
    setMarked([]);
    setSources([]);
    setActiveSupplierId(null);
    setSaveStatus('idle');
    setRestoreNotice(null);
    setError(null);
    if (draft) {
      const run = async () => {
        try {
          await loadEstimateService.discard(draft.id);
        } catch (e) {
          setError(errorMessage(e, t(`${T}.discardError`)));
        }
      };
      saveChain.current = saveChain.current.then(run, run);
      await saveChain.current;
    }
    await queryClient.invalidateQueries({ queryKey: ['loadEstimate', 'drafts'] });
  };

  // Descarta el borrador guardado que no se retomó, para estimar los proveedores elegidos sin reemplazarlo.
  const discardHeldDraft = async () => {
    const draft = heldDraft;
    if (!draft) return;
    try {
      await loadEstimateService.discard(draft.id);
    } catch (e) {
      setError(errorMessage(e, t(`${T}.discardError`)));
      return;
    }
    if (draftRef.current?.id === draft.id) {
      draftRef.current = null;
      lastSaved.current = null;
    }
    setHeldDraft(null);
    await queryClient.invalidateQueries({ queryKey: ['loadEstimate', 'drafts'] });
  };

  // Filas de la tabla: cifras de la estimación de cada proveedor o, mientras no la tiene, las del pedido.
  const tableRows: LoadEstimateRow[] = rowIds.map((supplierId) => {
    const candidate = candidates.find((row) => row.supplier_id === supplierId);
    const source = sources.find((item) => item.supplierId === supplierId);
    const totals = source ? totalsOf(source, candidate?.value ?? 0) : null;
    return {
      supplier_id: supplierId,
      supplier_name: candidate?.supplier_name ?? source?.supplierName ?? String(supplierId),
      kind: pending.some((row) => row.supplier_id === supplierId) ? 'validated' : 'suggested',
      source,
      products: totals?.products ?? candidate?.products ?? 0,
      amount: totals?.amount ?? candidate?.value ?? 0,
      volume: totals?.volume ?? null,
      volumePct: totals?.volumePct ?? null,
      weight: totals?.weight ?? null,
      weightPct: totals?.weightPct ?? null,
      boxes: totals?.boxes ?? 0,
      pallets: totals?.pallets ?? 0,
      usableVolume: totals?.usableVolume ?? 0,
      usableWeight: totals?.usableWeight ?? 0,
    };
  });

  return {
    warehouseId,
    enabled,
    requestedSupplierIds,
    loading: pendingQuery.isLoading || awaitingQuery.isLoading,
    pendingLoading: pendingQuery.isLoading,
    awaiting,
    candidates,
    templates,
    abcByProduct,
    rowIds,
    tableRows,
    marked,
    setMarked,
    toggleMarked,
    addSupplier,
    removeSupplier,
    sources,
    setSources,
    activeSupplierId,
    setActiveSupplierId,
    calculating,
    creating,
    error,
    created,
    saveStatus,
    restoreNotice,
    setRestoreNotice,
    heldDraft,
    setHeldDraft,
    draftRef,
    calculateSuppliers,
    changeContainer,
    saveProductConfig,
    restoreDraft,
    discardHeldDraft,
    discardEstimate,
    createOrder,
    productConflicts,
  };
}

export type LoadEstimate = ReturnType<typeof useLoadEstimate>;
